import React from "react";
import PropTypes from "prop-types";
import { useGetChatEligibilityQuery } from "@api/chatManagement";
import { ShareChannelModal } from "@components/Chat/components/ShareChannelModal";
import { useShareToChat } from "@components/Chat/hooks/useShareToChat";
import { canShareToChat, useChatAvailability, useIsLoggedIn } from "@components/Chat/utils";
import { BettoShareChannelModal } from "@layouts/betto/components/ShareChannelModal";
import { ShareButton, ShareButtonLabel } from "./elements";

const ShareIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true">
		<path
			d="M6.746704,4 L10.2109085,4 C10.625122,4 10.9609085,4.33578644 10.9609085,4.75 C10.9609085,5.12969577 10.6787546,5.44349096 10.312679,5.49315338 L10.2109085,5.5 L6.746704,5.5 C5.55584001,5.5 4.58105908,6.42516159 4.50189481,7.59595119 L4.496704,7.75 L4.496704,17.25 C4.496704,18.440864 5.42186559,19.4156449 6.59265519,19.4948092 L6.746704,19.5 L16.247437,19.5 C17.438301,19.5 18.4130819,18.5748384 18.4922462,17.4040488 L18.497437,17.25 L18.497437,16.752219 C18.497437,16.3380054 18.8332234,16.002219 19.247437,16.002219 C19.6271328,16.002219 19.940928,16.2843728 19.9905904,16.6504484 L19.997437,16.752219 L19.997437,17.25 C19.997437,19.2542592 18.4250759,20.8912737 16.4465956,20.994802 L16.247437,21 L6.746704,21 C4.74244483,21 3.10543026,19.4276389 3.00190201,17.4491586 L2.996704,17.25 L2.996704,7.75 C2.996704,5.74574083 4.56906505,4.10872626 6.54754543,4.00519801 L6.746704,4 L10.2109085,4 L6.746704,4 Z M14.5006976,6.54430631 L14.5006976,3.75 C14.5006976,3.12602964 15.20748,2.7899466 15.6876724,3.13980165 L15.7698701,3.20874226 L21.7644714,8.95874226 C22.0442311,9.22708681 22.0696965,9.65811353 21.8408438,9.95607385 L21.7645584,10.0411742 L15.7699571,15.7930263 C15.3196822,16.2250675 14.5877784,15.9476738 14.5078455,15.3589039 L14.5006976,15.2518521 L14.5006976,12.4499835 L14.1799379,12.4437673 C11.5224061,12.4359053 9.2508447,13.5269198 7.31506917,15.745002 C6.81945864,16.3128907 5.88979801,15.876896 6.00952162,15.1327229 C6.83651469,9.99233371 9.60859008,7.08827771 14.1987622,6.57442791 L14.5006976,6.54430631 L14.5006976,3.75 L14.5006976,6.54430631 Z"
			fill="currentColor"
		/>
	</svg>
);

const ShareLatestWin = ({ translate }) => {
	const isLoggedIn = useIsLoggedIn();
	const { isChatEnabled } = useChatAvailability();
	const { data: eligibility, isLoading: eligibilityLoading } = useGetChatEligibilityQuery(
		undefined,
		{
			skip: !isLoggedIn,
		}
	);
	const {
		openLatestCasinoWinShare,
		closeShare,
		confirmShare,
		isSharing,
		isShareModalOpen,
		shareModalTitleKey,
		enabledChannels,
		selectedChannelId,
		setSelectedChannelId,
		canConfirmShare,
	} = useShareToChat({ translate });

	const showShareButton = canShareToChat({
		isLoggedIn,
		isChatEnabled,
		eligibility,
		eligibilityLoading,
	});

	if (!showShareButton) {
		return null;
	}

	const ShareModal = window.TENANT === "betto" ? BettoShareChannelModal : ShareChannelModal;

	return (
		<>
			<ShareButton
				type="button"
				className="share-btn"
				onClick={openLatestCasinoWinShare}
				disabled={isSharing}
				data-testid="button-game-share-latest-win"
				aria-label={translate("chat.shareLatestWin")}>
				<ShareIcon />
				<ShareButtonLabel>{translate("chat.shareLatestWin")}</ShareButtonLabel>
			</ShareButton>
			<ShareModal
				isOpen={isShareModalOpen}
				translate={translate}
				titleKey={shareModalTitleKey}
				channels={enabledChannels}
				selectedChannelId={selectedChannelId}
				onChannelChange={setSelectedChannelId}
				isSubmitting={isSharing}
				canSubmit={canConfirmShare}
				onClose={closeShare}
				onSubmit={confirmShare}
			/>
		</>
	);
};

ShareLatestWin.propTypes = {
	translate: PropTypes.func.isRequired,
};

export default ShareLatestWin;
